import { AnyAction, combineReducers, configureStore } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';
import {
  ThemeContractService,
  ChannelsService,
  CsvTemplatesService,
  AIModelService,
  ActionLogsService,
  AuthorizedAppsService,
  AccountService,
  AuthService,
  BusinessDetailService,
  CampaignService,
  CatiCallService,
  CompanyService,
  ContactDetailService,
  ContactPolicyService,
  ContentCategoriesService,
  CustomerListService,
  CustomersService,
  DepartmentsService,
  ExDashboardService,
  FeedbackProviderService,
  FeedbackService,
  FileService,
  FlowTemplatesService,
  FlowsService,
  IntelligentTagService,
  JourneyMapApi,
  LanguagesService,
  LeoService,
  LlmManagementService,
  MobileApplicationsService,
  NodesService,
  NotificationsService,
  PlannerApi,
  ReportWizardService,
  ReportsDetailService,
  ReportsService,
  SchemaFieldsService,
  SegmentsService,
  SmsProviderService,
  TagService,
  TableDispatchService,
  TicketManagerApi,
  UserGroupsService,
  UserProfileService,
  UsersService,
  VisionResourcesService,
  WebWidgetsService,
  WorkflowsService,
  ELCSurveyTriggerLogsService,
} from '@vision/ui/services';
import { ChannelStylesheetsService } from '@vision/ui/services/channel-stylesheets/channel-stylesheets.service';
import { ChannelThemesService } from '@vision/ui/services/channel-themes/channel-themes.service';
import { __DEV__ } from '@vision/ui/utils';
import { persistReducer, persistStore } from 'redux-persist';
import createFilter from 'redux-persist-transform-filter';
import storage from 'redux-persist/lib/storage';
import { StateResetAction } from './actions';
import { ListenerMiddleware } from './middlewares';
import {
  AuthSsoState,
  AuthState,
  AuthStateType,
  ChannelsState,
  ContactsState,
  ContentCategoriesState,
  IntelligentTagsState,
  LanguagesState,
  LeoState,
  JourneyMapState,
  NotificationState,
  PlannerState,
  ReportsDetailGraphicState,
  ReportsDetailState,
  AtaDashboardState,
  ReportsState,
  SmsProviderState,
  TicketManagerSettingsState,
  UploadProgressState,
  WorkflowsState,
  SurveyLogicBuilderState,
} from './states';

const STATES = {
  AuthSsoState,
  AuthState,
  ChannelsState,
  ContactsState,
  ContentCategoriesState,
  LanguagesState,
  LeoState,
  JourneyMapState,
  PlannerState,
  SmsProviderState,
  TicketManagerSettingsState,
  NotificationState,
  ReportsDetailGraphicState,
  ReportsState,
  AtaDashboardState,
  ReportsDetailState,
  UploadProgressState,
  WorkflowsState,
  IntelligentTagsState,
  SurveyLogicBuilderState,
};

const MIDDLEWARES = [
  ThemeContractService.middleware,
  ChannelsService.middleware,
  ChannelStylesheetsService.middleware,
  ChannelThemesService.middleware,
  CsvTemplatesService.middleware,
  AccountService.middleware,
  ActionLogsService.middleware,
  AuthorizedAppsService.middleware,
  AuthService.middleware,
  BusinessDetailService.middleware,
  CampaignService.middleware,
  CompanyService.middleware,
  ContactDetailService.middleware,
  ContactPolicyService.middleware,
  CustomerListService.middleware,
  ContentCategoriesService.middleware,
  CustomersService.middleware,
  FeedbackService.middleware,
  FeedbackProviderService.middleware,
  FlowsService.middleware,
  FlowTemplatesService.middleware,
  LanguagesService.middleware,
  LeoService.middleware,
  MobileApplicationsService.middleware,
  NodesService.middleware,
  NotificationsService.middleware,
  PlannerApi.middleware,
  ReportsService.middleware,
  ReportWizardService.middleware,
  SchemaFieldsService.middleware,
  SegmentsService.middleware,
  TagService.middleware,
  TicketManagerApi.middleware,
  SmsProviderService.middleware,
  UserGroupsService.middleware,
  UserProfileService.middleware,
  UsersService.middleware,
  VisionResourcesService.middleware,
  WebWidgetsService.middleware,
  ELCSurveyTriggerLogsService.middleware,
  WorkflowsService.middleware,
  FileService.middleware,
  CatiCallService.middleware,
  ReportsDetailService.middleware,
  TableDispatchService.middleware,
  ExDashboardService.middleware,
  DepartmentsService.middleware,
  IntelligentTagService.middleware,
  JourneyMapApi.middleware,
  AIModelService.middleware,
  LlmManagementService.middleware,
];

const SERVICES = {
  [ThemeContractService.reducerPath]: ThemeContractService.reducer,
  [ChannelsService.reducerPath]: ChannelsService.reducer,
  [ChannelStylesheetsService.reducerPath]: ChannelStylesheetsService.reducer,
  [ChannelThemesService.reducerPath]: ChannelThemesService.reducer,
  [CsvTemplatesService.reducerPath]: CsvTemplatesService.reducer,
  [AccountService.reducerPath]: AccountService.reducer,
  [ActionLogsService.reducerPath]: ActionLogsService.reducer,
  [AuthorizedAppsService.reducerPath]: AuthorizedAppsService.reducer,
  [AuthService.reducerPath]: AuthService.reducer,
  [BusinessDetailService.reducerPath]: BusinessDetailService.reducer,
  [CampaignService.reducerPath]: CampaignService.reducer,
  [CompanyService.reducerPath]: CompanyService.reducer,
  [ContactDetailService.reducerPath]: ContactDetailService.reducer,
  [ContactPolicyService.reducerPath]: ContactPolicyService.reducer,
  [ContentCategoriesService.reducerPath]: ContentCategoriesService.reducer,
  [CustomerListService.reducerPath]: CustomerListService.reducer,
  [CustomersService.reducerPath]: CustomersService.reducer,
  [FeedbackService.reducerPath]: FeedbackService.reducer,
  [FeedbackProviderService.reducerPath]: FeedbackProviderService.reducer,
  [FlowsService.reducerPath]: FlowsService.reducer,
  [FlowTemplatesService.reducerPath]: FlowTemplatesService.reducer,
  [LanguagesService.reducerPath]: LanguagesService.reducer,
  [LeoService.reducerPath]: LeoService.reducer,
  [MobileApplicationsService.reducerPath]: MobileApplicationsService.reducer,
  [NodesService.reducerPath]: NodesService.reducer,
  [NotificationsService.reducerPath]: NotificationsService.reducer,
  [PlannerApi.reducerPath]: PlannerApi.reducer,
  [ReportsService.reducerPath]: ReportsService.reducer,
  [ReportWizardService.reducerPath]: ReportWizardService.reducer,
  [SchemaFieldsService.reducerPath]: SchemaFieldsService.reducer,
  [SegmentsService.reducerPath]: SegmentsService.reducer,
  [SmsProviderService.reducerPath]: SmsProviderService.reducer,
  [TagService.reducerPath]: TagService.reducer,
  [TicketManagerApi.reducerPath]: TicketManagerApi.reducer,
  [UserGroupsService.reducerPath]: UserGroupsService.reducer,
  [UserProfileService.reducerPath]: UserProfileService.reducer,
  [UsersService.reducerPath]: UsersService.reducer,
  [VisionResourcesService.reducerPath]: VisionResourcesService.reducer,
  [WebWidgetsService.reducerPath]: WebWidgetsService.reducer,
  [WorkflowsService.reducerPath]: WorkflowsService.reducer,
  [ELCSurveyTriggerLogsService.reducerPath]: ELCSurveyTriggerLogsService.reducer,
  [FileService.reducerPath]: FileService.reducer,
  [CatiCallService.reducerPath]: CatiCallService.reducer,
  [ReportsDetailService.reducerPath]: ReportsDetailService.reducer,
  [TableDispatchService.reducerPath]: TableDispatchService.reducer,
  [ExDashboardService.reducerPath]: ExDashboardService.reducer,
  [DepartmentsService.reducerPath]: DepartmentsService.reducer,
  [IntelligentTagService.reducerPath]: IntelligentTagService.reducer,
  [JourneyMapApi.reducerPath]: JourneyMapApi.reducer,
  [AIModelService.reducerPath]: AIModelService.reducer,
  [LlmManagementService.reducerPath]: LlmManagementService.reducer,
};

const REDUCERS = {
  AuthSsoState: AuthSsoState.reducer,
  AuthState: AuthState.reducer,
  ChannelsState: ChannelsState.reducer,
  ContactsState: ContactsState.reducer,
  ContentCategoriesState: ContentCategoriesState.reducer,
  LanguagesState: LanguagesState.reducer,
  LeoState: LeoState.reducer,
  JourneyMapState: JourneyMapState.reducer,
  PlannerState: PlannerState.reducer,
  SmsProviderState: SmsProviderState.reducer,
  TicketManagerSettingsState: TicketManagerSettingsState.reducer,
  NotificationState: NotificationState.reducer,
  ReportsDetailGraphicState: ReportsDetailGraphicState.reducer,
  ReportsState: ReportsState.reducer,
  ReportsDetailState: ReportsDetailState.reducer,
  AtaDashboardState: AtaDashboardState.reducer,
  UploadProgressState: UploadProgressState.reducer,
  WorkflowsState: WorkflowsState.reducer,
  IntelligentTagsState: IntelligentTagsState.reducer,
  SurveyLogicBuilderState: SurveyLogicBuilderState.reducer,
};

const COMBINED_REDUCERS = combineReducers({
  ...SERVICES,
  ...REDUCERS,
});

/*
 * Save only 'token' and 'id' to localStorage for user authentication
 */
const FilteredAuthState = createFilter<
  { token: string; id: string },
  AuthStateType,
  ReturnType<typeof COMBINED_REDUCERS>,
  ReturnType<typeof COMBINED_REDUCERS>
>(AuthState.name, ['token', 'id', 'selectedAccountId', 'localizationSettings'] as (keyof AuthStateType)[]);

const persistedReducer = persistReducer(
  {
    key: 'vision',
    version: 1,
    storage,
    whitelist: [AuthState.name],
    transforms: [FilteredAuthState],
  },
  COMBINED_REDUCERS,
);

const rootReducer = (state: any, action: AnyAction) => {
  if (action.type === StateResetAction.type) {
    const stateKey = action.payload! as StateKeys;

    return persistedReducer(
      {
        ...state,
        [stateKey]: STATES[stateKey].getInitialState(),
      },
      action,
    );
  }
  return persistedReducer(state, action);
};

export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
    })
      .prepend(ListenerMiddleware.middleware)
      .concat(MIDDLEWARES),
  devTools: __DEV__,
});

export const persistor = persistStore(store);

export type AppDispatch = typeof store.dispatch;
export type RootState = Pick<ReturnType<typeof store.getState>, StateKeys>;
export type StateKeys = keyof typeof STATES;

setupListeners(store.dispatch);
